import type { Ring } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import { panelWall } from '../../panels';
import { edgePanel, rectWall, type MasonryStyles } from '../masonry';
import { gableOutline, transverseRoof } from './roofs';

/**
 * A gabled dormer on a roof slope facing -v: its front stands at v with a window, it runs back
 * `depth` into the roof, from y0 (below the slope, inside the hall) to its eaves and ridge.
 */
export interface DormerSpec {
  readonly u: number;
  readonly v: number;
  readonly width: number;
  readonly depth: number;
  readonly y0: number;
  readonly eaveY: number;
  readonly ridgeY: number;
}

/* A small window under the dormer's eaves, clear of the slope it stands on. */
const WINDOW = { share: 0.5, height: 0.95, topBelow: 0.25 } as const;

export function dormerSpan(d: DormerSpec): readonly [number, number] {
  return [d.u - d.width / 2, d.u + d.width / 2];
}

export function dormerSolid(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, d: DormerSpec): void {
  const [u0, u1] = dormerSpan(d);
  const v1 = d.v + d.depth;
  const w = d.width * WINDOW.share;
  const top = d.eaveY - WINDOW.topBelow;
  const sill = top - WINDOW.height;
  const window: Ring = [
    [(d.width - w) / 2, sill],
    [(d.width + w) / 2, sill],
    [(d.width + w) / 2, top],
    [(d.width - w) / 2, top],
  ];
  panelWall(builder, styles, frame, edgePanel([u0, d.v], [u1, d.v]), gableOutline(d.width, d.y0, d.eaveY, d.ridgeY), [window]);
  panelWall(builder, styles, frame, edgePanel([u1, v1], [u0, v1]), gableOutline(d.width, d.y0, d.eaveY, d.ridgeY), []);
  rectWall(builder, styles, frame, [[u1, d.v], [u1, v1]], [d.y0, d.eaveY]);
  rectWall(builder, styles, frame, [[u0, v1], [u0, d.v]], [d.y0, d.eaveY]);
  transverseRoof(builder, styles, frame, { u0, u1, v0: d.v, v1 }, { eaveY: d.eaveY, ridgeY: d.ridgeY });
}
